import React from 'react';

import { canViewInstanceAnalytics } from '@lumapps/analytics/ducks/selectors';
import { isBeekeeperMainPlatform } from '@lumapps/beekeeper/ducks/selectors';
import { canAccessBroadcast } from '@lumapps/broadcasts/ducks/selectors';
import { actions as communitiesActions } from '@lumapps/communities/ducks/frontOfficeSlice';
import { isCommunitiesAllowed, isNewCommunityNavigationEnabled } from '@lumapps/communities/ducks/selectors';
import {
    doesCurrentUserHaveAccessToCustomContentTypes,
    getAccessibleCustomContentTypes,
    getCustomContentTypes,
    getParentCustomContentTypeIds,
    isCustomContentTypesLoading,
} from '@lumapps/content-types/ducks/selectors';
import { fetchCustomContentTypes } from '@lumapps/content-types/ducks/thunks';
import { getInstanceDirectories, isDirectoriesAllowed, isModuleAllowed } from '@lumapps/directories/ducks/selectors';
import { isAdminSettingsAllowed } from '@lumapps/instance-settings/ducks/selectors';
import { canAccessJourneysAdmin } from '@lumapps/journeys-admin/ducks/selectors';
import { canAccessMediaLibrary } from '@lumapps/medias/ducks/selectors';
import { isMetadataEditAllowed } from '@lumapps/metadata/ducks/selectors';
import { hasMainNavAdminAccess } from '@lumapps/navigation-admin/ducks/selectors';
import { isNewsletterAllowed } from '@lumapps/newsletter/ducks/selectors';
import { isInstanceSuperAdmin, isSuperAdmin } from '@lumapps/permissions';
import { useDispatch, useSelector } from '@lumapps/redux/react';
import {
    canManagePlaftormPrograms,
    canManagePlatformSocialNetworks,
    isSAEnabled,
} from '@lumapps/sa-connected-user/ducks/selectors';
import { canAccessSegmentAdmin } from '@lumapps/segment/ducks/slice/selectors';
import { isSpaceEditableByUser } from '@lumapps/spaces/ducks/selectors';
import { actions as spaceActions } from '@lumapps/spaces/ducks/slice';
import { isUserDirectoryEditAllowed } from '@lumapps/user-directory-back-office/ducks/selectors';
import { isConnected } from '@lumapps/user/ducks/selectors';

import { SettingsMenu as SettingsMenuUI, SettingsMenuProps as SettingsMenuUIProps } from './components/SettingsMenu';

type ConnectedProps = Omit<
    SettingsMenuUIProps,
    | 'isVisible'
    | 'isNewCommunityNavigationEnabled'
    | 'canEditSpace'
    | 'isLoading'
    | 'customContentTypes'
    | 'parentCustomContentTypesIds'
    | 'isSuperAdmin'
    | 'allowedCustomContentTypes'
    | 'isAllowedToAccessCCT'
    | 'canAccessModules'
    | 'canAccessCommunities'
    | 'canEditDirectories'
    | 'directories'
    | 'canEditNewsletter'
    | 'canAccessUserDirectories'
    | 'SAEnabled'
    | 'userCanManagePlatformPrograms'
    | 'userCanManagePlatformSocialNetworks'
    | 'canAccessBroadcast'
    | 'canAccessJourneys'
    | 'canAccessSegment'
    | 'canAccessContentStream'
    | 'canAccessSiteAnalytics'
    | 'hasMainNavAdminAccess'
    | 'canEditInstance'
    | 'hasMetadataAdminRights'
    | 'canAccessMediaLibrary'
    | 'fetchCustomContentTypes'
    | 'onCommunityConfigurationEdit'
    | 'onCommunityNavigationEdit'
    | 'onSpaceConfigurationEdit'
    | 'onSpaceNavigationEdit'
>;

export const SettingsMenu: React.FC<ConnectedProps> = (props) => {
    const dispatch = useDispatch();

    const isVisible = useSelector(isConnected);
    const isNewCommunityNavigationEnabledValue = useSelector(isNewCommunityNavigationEnabled);
    const canEditSpace = useSelector(isSpaceEditableByUser);
    const isLoading = useSelector(isCustomContentTypesLoading);
    const customContentTypes = useSelector(getCustomContentTypes);
    const parentCustomContentTypesIds = useSelector(getParentCustomContentTypeIds);
    const isSuperAdminValue = useSelector(isInstanceSuperAdmin);
    const accessibleCustomContentTypes = useSelector(getAccessibleCustomContentTypes);
    const isAllowedToAccessCCT = useSelector(doesCurrentUserHaveAccessToCustomContentTypes);
    const canAccessModules = useSelector(isModuleAllowed);
    const canAccessCommunities = useSelector(isCommunitiesAllowed);
    const canEditDirectories = useSelector(isDirectoriesAllowed);
    const directories = useSelector(getInstanceDirectories);
    const canEditNewsletter = useSelector(isNewsletterAllowed);
    const canAccessUserDirectories = useSelector(isUserDirectoryEditAllowed);
    const SAEnabled = useSelector(isSAEnabled);
    const userCanManagePlatformPrograms = useSelector(canManagePlaftormPrograms);
    const userCanManagePlatformSocialNetworks = useSelector(canManagePlatformSocialNetworks);
    const canAccessBroadcastValue = useSelector(canAccessBroadcast);
    const canAccessJourneys = useSelector(canAccessJourneysAdmin);
    const canAccessSegmentAdminValue = useSelector(canAccessSegmentAdmin);
    const isBeekeeperMainPlatformValue = useSelector(isBeekeeperMainPlatform);
    // Beekeeper owns segment administration when it is the customer's main platform.
    const canAccessSegment = canAccessSegmentAdminValue && !isBeekeeperMainPlatformValue;
    const canAccessContentStream = useSelector(isSuperAdmin);
    const canAccessSiteAnalytics = useSelector(canViewInstanceAnalytics);
    const hasMainNavAdminAccessValue = useSelector(hasMainNavAdminAccess);
    const canEditInstance = useSelector(isAdminSettingsAllowed);
    const hasMetadataAdminRights = useSelector(isMetadataEditAllowed);
    const canAccessMediaLibraryValue = useSelector(canAccessMediaLibrary);

    const handleFetchCustomContentTypes = React.useCallback(() => {
        dispatch(fetchCustomContentTypes());
    }, [dispatch]);

    const handleCommunityConfigurationEdit = React.useCallback(() => {
        dispatch(communitiesActions.openConfigurationWizard());
    }, [dispatch]);

    const handleCommunityNavigationEdit = React.useCallback(() => {
        dispatch(communitiesActions.setCommunityNavigationEdit({ isOpen: true }));
    }, [dispatch]);

    const handleSpaceConfigurationEdit = React.useCallback(() => {
        dispatch(communitiesActions.openConfigurationWizard());
    }, [dispatch]);

    const handleSpaceNavigationEdit = React.useCallback(() => {
        dispatch(spaceActions.openNavigationAdminDialog());
    }, [dispatch]);

    return (
        <SettingsMenuUI
            {...props}
            isVisible={isVisible}
            isNewCommunityNavigationEnabled={isNewCommunityNavigationEnabledValue}
            canEditSpace={canEditSpace}
            isLoading={isLoading}
            customContentTypes={customContentTypes}
            parentCustomContentTypesIds={parentCustomContentTypesIds}
            isSuperAdmin={isSuperAdminValue}
            allowedCustomContentTypes={accessibleCustomContentTypes}
            isAllowedToAccessCCT={isAllowedToAccessCCT}
            canAccessModules={canAccessModules}
            canAccessCommunities={canAccessCommunities}
            canEditDirectories={canEditDirectories}
            directories={directories}
            canEditNewsletter={canEditNewsletter}
            canAccessUserDirectories={canAccessUserDirectories}
            SAEnabled={SAEnabled}
            userCanManagePlatformPrograms={userCanManagePlatformPrograms}
            userCanManagePlatformSocialNetworks={userCanManagePlatformSocialNetworks}
            canAccessBroadcast={canAccessBroadcastValue}
            canAccessJourneys={canAccessJourneys}
            canAccessSegment={canAccessSegment}
            canAccessContentStream={canAccessContentStream}
            canAccessSiteAnalytics={canAccessSiteAnalytics}
            hasMainNavAdminAccess={hasMainNavAdminAccessValue}
            canEditInstance={canEditInstance}
            hasMetadataAdminRights={hasMetadataAdminRights}
            canAccessMediaLibrary={canAccessMediaLibraryValue}
            fetchCustomContentTypes={handleFetchCustomContentTypes}
            onCommunityConfigurationEdit={handleCommunityConfigurationEdit}
            onCommunityNavigationEdit={handleCommunityNavigationEdit}
            onSpaceConfigurationEdit={handleSpaceConfigurationEdit}
            onSpaceNavigationEdit={handleSpaceNavigationEdit}
        />
    );
};

SettingsMenu.displayName = 'SettingsMenuConnected';
